import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';
import { apiRequest } from '../../services/api';
import ChatLayout from '../../components/Layout/ChatLayout';
import Sidebar from '../../components/Sidebar/Sidebar';
import MetricsCard from '../../components/Shared/MetricsCard';
import ProgressTracker from '../../components/ProgressTracker';
import AchievementBadges from '../../components/AchievementBadges';
import Skeleton from '../../components/Shared/Skeleton';
import {
  ChatCircle,
  BookOpen,
  Star,
  Lightbulb,
  Target,
  Rocket,
  HandWaving,
  PencilSimple,
} from '@phosphor-icons/react';

const StudentDashboard = () => {
  const { currentUser } = useAuth();
  const {
    sessions,
    currentSessionId,
    setCurrentSessionId,
    deleteSession,
    clearAllSessions
  } = useChat();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [sessionSearch, setSessionSearch] = useState('');
  const [renamingSession, setRenamingSession] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [funFactIndex, setFunFactIndex] = useState(0);

  const gradeLevel = dashboardData?.student?.grade_level || currentUser?.grade_level || 'middle';
  const isElementary = gradeLevel === 'elementary';

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      let url = '/api/student/dashboard';
      if (selectedClassId) url += `?class_section_id=${selectedClassId}`;
      const data = await apiRequest(url, 'GET');
      setDashboardData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedClassId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const enrolledClasses = dashboardData?.enrolled_classes || [];
  const isMultiClass = enrolledClasses.length > 1;

  const handleRenameSession = async (sessionId) => {
    if (!renameValue.trim()) return;
    try {
      await apiRequest(`/api/chat/sessions/${sessionId}`, 'PUT', { title: renameValue.trim() });
      fetchDashboard();
      setRenamingSession(null);
      setRenameValue('');
    } catch (err) {
      console.error('Failed to rename session:', err);
    }
  };

  const handleNewChat = () => {
    setCurrentSessionId(null);
    navigate('/student/chat');
  };

  const renderContent = () => {
    if (loading) {
      return <Skeleton.DashboardSkeleton isElementary={currentUser?.grade_level === 'elementary'} />;
    }

    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading dashboard: {error}
        </div>
      );
    }

    const filteredChats = (dashboardData?.recent_chats || []).filter(chat =>
      !sessionSearch || chat.title.toLowerCase().includes(sessionSearch.toLowerCase())
    );
    const firstName = dashboardData?.student?.first_name || dashboardData?.student?.display_name || 'there';
    const funFacts = dashboardData?.fun_facts || [];

    return (
      <>
        {/* Hero Section - merged Welcome + CTA */}
        <div
          className="bg-gradient-to-r from-indigo-500 to-indigo-700 rounded-xl p-6 mb-6 text-white bg-[length:200%_200%] animate-gradient-shift"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              {isElementary ? (
                <h2 className="text-2xl sm:text-3xl font-bold mb-1 flex items-center gap-2">
                  Hi {firstName}! <HandWaving size={28} weight="fill" />
                </h2>
              ) : (
                <h2 className="text-2xl sm:text-3xl font-bold mb-1">
                  Hey {firstName}!
                </h2>
              )}
              <p className="text-indigo-100 text-sm">
                {dashboardData?.teacher?.name && `${dashboardData.teacher.name}'s Class`}
                {dashboardData?.teacher?.subject && ` \u2022 ${dashboardData.teacher.subject}`}
                {dashboardData?.school?.name && ` \u2022 ${dashboardData.school.name}`}
              </p>
              <p className="text-indigo-100 mt-2">
                {isElementary
                  ? 'Ask your AI tutor anything \u2014 it loves helping you learn!'
                  : 'Your AI tutor is here to help with homework, questions, or learning new topics!'}
              </p>
            </div>
            <button
              onClick={() => navigate('/student/chat')}
              className={`bg-white text-indigo-600 rounded-lg font-semibold hover:bg-indigo-50 transition-colors shadow-lg flex-shrink-0 flex items-center gap-2 ${
                isElementary ? 'px-8 py-4 text-lg' : 'px-6 py-3'
              }`}
              aria-label="Start learning"
            >
              {isElementary && <Rocket size={22} weight="fill" />}
              Start Learning{isElementary ? '!' : ''}
            </button>
          </div>
        </div>

        {/* Multi-Class Selector */}
        {isMultiClass && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-600 mb-3">Your Classes</h3>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setSelectedClassId(null)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !selectedClassId
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-white border border-gray-200 text-gray-700 hover:border-indigo-300'
                }`}
              >
                All Classes
              </button>
              {enrolledClasses.map(cls => (
                <button
                  key={cls.class_section_id}
                  onClick={() => setSelectedClassId(cls.class_section_id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedClassId === cls.class_section_id
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'bg-white border border-gray-200 text-gray-700 hover:border-indigo-300'
                  }`}
                >
                  {cls.subject}{cls.period_number ? ` (P${cls.period_number})` : ''}
                  <span className="block text-xs opacity-75">{cls.teacher_name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Single-class header */}
        {!isMultiClass && enrolledClasses.length === 1 && (
          <div className="mb-4 text-sm text-indigo-700 bg-indigo-50 rounded-lg px-4 py-2 inline-block">
            {enrolledClasses[0].subject} with {enrolledClasses[0].teacher_name}
            {enrolledClasses[0].period_number ? ` \u2022 Period ${enrolledClasses[0].period_number}` : ''}
          </div>
        )}

        {/* Learning Stats */}
        <div className={`grid gap-6 mb-6 ${isElementary ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
          <MetricsCard
            title={isElementary ? 'Chats' : 'Learning Sessions'}
            value={dashboardData?.learning_stats?.total_sessions || 0}
            subtitle={isElementary ? 'completed' : 'chats completed'}
            icon={<ChatCircle size={22} weight="fill" />}
            valueClassName={isElementary ? 'text-4xl' : undefined}
          />
          {!isElementary && (
            <MetricsCard
              title="Topics Explored"
              value={dashboardData?.learning_stats?.topics_explored || 0}
              subtitle="different subjects"
              icon={<BookOpen size={22} weight="fill" />}
            />
          )}
          <MetricsCard
            title={isElementary ? 'Streak' : 'Learning Streak'}
            value={dashboardData?.learning_stats?.streak_days || 0}
            subtitle={isElementary ? 'days!' : 'days in a row'}
            trend={dashboardData?.learning_stats?.streak_days >= 7 ? 'On fire!' : 'Keep going!'}
            trendColor={dashboardData?.learning_stats?.streak_days >= 7 ? 'green' : 'yellow'}
            icon={<Star size={22} weight="fill" />}
            valueClassName={isElementary ? 'text-4xl' : undefined}
          />
        </div>

        {/* Compact Learning Tip - inline banner */}
        <div className="bg-blue-50 rounded-lg px-4 py-2.5 mb-6 flex items-center gap-3 animate-fade-in-up">
          <div className="flex-shrink-0 text-blue-600">
            <Lightbulb size={20} weight="fill" />
          </div>
          <p className="text-sm text-blue-800">
            {dashboardData?.learning_tip ||
              'The more questions you ask, the more you learn! Don\'t be afraid to explore new topics with your AI tutor.'}
          </p>
        </div>

        {/* Two-column: Achievements + Progress (desktop) */}
        <div className="grid gap-6 mb-6 grid-cols-1 lg:grid-cols-2">
          {dashboardData?.badges && (
            <AchievementBadges
              badges={dashboardData.badges}
              title={isElementary ? 'My Badges' : 'Achievements'}
            />
          )}
          <ProgressTracker classSectionId={selectedClassId} />
        </div>

        {/* Fun Fact - compact rotating single fact */}
        {funFacts.length > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3 mb-6 flex items-center gap-3">
            <div className="flex-shrink-0 text-indigo-600">
              <Target size={20} weight="fill" />
            </div>
            <p className="text-sm text-indigo-800 flex-1">{funFacts[funFactIndex % funFacts.length]}</p>
            {funFacts.length > 1 && (
              <button
                onClick={() => setFunFactIndex(i => i + 1)}
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex-shrink-0 px-2 py-1 rounded hover:bg-indigo-100 transition-colors"
                aria-label="Next fun fact"
              >
                Next
              </button>
            )}
          </div>
        )}

        {/* Recent Chats */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">
              {isElementary ? 'My Chats' : 'Recent Learning Sessions'}
            </h3>
          </div>
          <input
            type="text"
            placeholder="Search sessions..."
            value={sessionSearch}
            onChange={(e) => setSessionSearch(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3 focus:outline-none focus:border-indigo-500"
            aria-label="Search sessions"
          />
          {filteredChats.length > 0 ? (
            <div className="space-y-3">
              {filteredChats.map((chat, index) => (
                <div
                  key={index}
                  className="p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors cursor-pointer group"
                  onClick={() => {
                    if (renamingSession !== chat.session_id) {
                      navigate(`/student/chat?session=${chat.session_id}`);
                    }
                  }}
                >
                  <div className="flex justify-between items-start mb-1">
                    {renamingSession === chat.session_id ? (
                      <input
                        type="text"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRenameSession(chat.session_id);
                          if (e.key === 'Escape') { setRenamingSession(null); setRenameValue(''); }
                        }}
                        onBlur={() => { setRenamingSession(null); setRenameValue(''); }}
                        className="font-medium text-gray-900 border-b-2 border-indigo-500 outline-none bg-transparent flex-1 mr-2"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <p className="font-medium text-gray-900 flex-1">{chat.title}</p>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{chat.time_ago}</span>
                      {renamingSession !== chat.session_id && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setRenamingSession(chat.session_id);
                            setRenameValue(chat.title);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-indigo-600 transition-all"
                          aria-label="Rename session"
                          title="Rename"
                        >
                          <PencilSimple size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {chat.class_tag && (
                      <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">
                        {chat.class_tag.subject}
                      </span>
                    )}
                    <p className="text-sm text-gray-600">
                      {chat.message_count} message{chat.message_count !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p className="mb-3">{sessionSearch ? 'No matching sessions' : 'No chats yet!'}</p>
              {!sessionSearch && (
                <button
                  onClick={() => navigate('/student/chat')}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Start Your First Chat
                </button>
              )}
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <ChatLayout>
      <Sidebar
        sessions={sessions}
        currentSessionId={currentSessionId}
        onNewChat={handleNewChat}
        onSelectSession={(id) => {
          setCurrentSessionId(id);
          navigate('/student/chat');
        }}
        onDeleteSession={deleteSession}
        onClearAll={clearAllSessions}
      />
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {renderContent()}
        </div>
      </div>
    </ChatLayout>
  );
};

export default StudentDashboard;
